import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import {
  BackButtonComponent,
  PageHeaderComponent,
} from '../../shared/components/ui-shell.component';
import { HorizontalTimelineComponent } from '../../shared/components/timeline.component';
import { MonitoringService } from '../../services/monitoring.service';
import { DatDevice, DatTimelineSegment, TimeWindow } from '../../models/monitoring.models';
import { currentSlotMinutes } from '../../utils/time-utils';
import { HistoryDay, parseHistoryDay } from '../../utils/history-ranges';

@Component({
  selector: 'app-dat-list',
  standalone: true,
  imports: [PageHeaderComponent, BackButtonComponent, HorizontalTimelineComponent],
  template: `
    <div class="screen">
      <header class="top-bar">
        <app-page-header title="Etat des DAT de la gare" [subtitle]="stationName()" />
        <button
          type="button"
          class="btn-refresh"
          [class.spinning]="refreshing()"
          [disabled]="refreshing()"
          title="Actualiser"
          (click)="refresh()"
        >↻</button>
      </header>

      <section class="content">
        <div class="time-nav">
          <button type="button" class="nav-btn" (click)="shiftWindow(-15)" aria-label="Précédent">◀</button>
          <div class="time-window">
            <span class="clock" aria-hidden="true">🕐</span>
            <div class="time-labels">
              <span class="time-range">{{ window().startLabel }} - {{ window().endLabel }}</span>
              <span class="time-center">Créneau sélectionné : {{ window().centerLabel }}</span>
            </div>
          </div>
          <button
            type="button"
            class="nav-btn"
            [disabled]="isAtLatestSlot()"
            (click)="shiftWindow(15)"
            aria-label="Suivant"
          >▶</button>
        </div>

        <p class="slot-hint">Barres découpées par tranche de 15 min — le créneau choisi est encadré en bleu.</p>

        <div class="dat-list">
          @for (dat of dats(); track dat.id) {
            <div class="dat-row">
              <span class="dat-name">{{ dat.name }}</span>
              <app-horizontal-timeline
                [segments]="dat.segments"
                [windowStartMinutes]="window().startMinutes"
                [windowEndMinutes]="window().endMinutes"
                [windowStartLabel]="window().startLabel"
                [windowEndLabel]="window().endLabel"
                [centerLabel]="window().centerLabel"
                [selectedMinutes]="centerMinutes()"
                [splitIntoSlots]="true"
                [nowMinutes]="nowMinutes()"
                (segmentClick)="onSegmentClick(dat, $event)"
              />
            </div>
          }
        </div>

        <p class="scroll-hint" aria-hidden="true">↓</p>
      </section>

      <app-back-button [backFn]="goBack.bind(this)" />
    </div>
  `,
  styles: [
    `
      .screen {
        min-height: 100%;
        display: flex;
        flex-direction: column;
      }

      .top-bar {
        position: relative;
      }

      .btn-refresh {
        position: absolute;
        top: 1rem;
        right: 1rem;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: 50%;
        border: 1px solid #888;
        background: #fff;
        font-size: 1.25rem;
        cursor: pointer;
        line-height: 1;
      }

      .btn-refresh:disabled {
        cursor: default;
        opacity: 0.6;
      }

      .btn-refresh.spinning {
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .content {
        padding: 0 1rem 1rem;
        flex: 1;
      }

      .time-nav {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.25rem;
        padding: 0.5rem 0;
      }

      .nav-btn {
        border: 1px solid #888;
        background: #fff;
        border-radius: 50%;
        width: 2rem;
        height: 2rem;
        cursor: pointer;
        font-size: 0.75rem;
      }

      .nav-btn:disabled {
        cursor: default;
        opacity: 0.4;
      }

      .time-window {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        font-weight: 600;
        font-size: 0.95rem;
        padding: 0.35rem 0.75rem;
        border: 1px solid #ccc;
        border-radius: 999px;
        background: #fafafa;
      }

      .time-labels {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.1rem;
        line-height: 1.2;
      }

      .time-range {
        font-size: 0.9rem;
      }

      .time-center {
        font-size: 0.75rem;
        font-weight: 700;
        color: #1565c0;
      }

      .slot-hint {
        text-align: center;
        font-size: 0.7rem;
        color: #777;
        margin: -0.75rem 0 1rem;
        padding: 0 0.5rem;
      }

      .clock {
        font-size: 0.9rem;
      }

      .dat-list {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }

      .dat-row {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
      }

      .dat-name {
        font-weight: 700;
        font-size: 0.95rem;
      }

      .scroll-hint {
        text-align: center;
        color: #888;
        margin: 1rem 0 0;
        font-size: 1.25rem;
      }
    `,
  ],
})
export class DatListComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly monitoring = inject(MonitoringService);

  readonly stationId = signal('');
  readonly stationName = signal('');
  readonly dats = signal<DatDevice[]>([]);
  readonly centerMinutes = signal(currentSlotMinutes());
  readonly window = signal<TimeWindow>(this.monitoring.getDatWindow(currentSlotMinutes()));
  readonly selectedDay = signal<HistoryDay>('today');
  readonly refreshing = signal(false);
  /**
   * Borne « pas de données au-delà » (minutes UTC, référentiel back) : l'heure courante
   * quand on consulte aujourd'hui, aucune borne pour hier (journée déjà entièrement passée).
   * Recalculée à chaque navigation/rafraîchissement — cf. ticket décalage de 2h.
   */
  readonly nowMinutes = signal<number | null>(currentSlotMinutes());

  constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([params, query]) => ({
          stationId: params.get('stationId') ?? '',
          time: Number(query.get('time') ?? currentSlotMinutes()),
          day: parseHistoryDay(query.get('day')),
        })),
      )
      .subscribe(({ stationId, time, day }) => {
        this.stationId.set(stationId);
        this.selectedDay.set(day);
        this.nowMinutes.set(this.computeNowMinutes(day));
        this.centerMinutes.set(time);
        this.window.set(this.monitoring.getDatWindow(time));
        this.monitoring.getStation(stationId).subscribe((station) => {
          this.stationName.set(station?.name ?? stationId);
        });
        this.loadDats(stationId, time, day);
      });
  }

  private computeNowMinutes(day: HistoryDay): number | null {
    return day === 'today' ? currentSlotMinutes() : null;
  }

  isAtLatestSlot(): boolean {
    const bound = this.nowMinutes();
    return bound != null && this.centerMinutes() >= bound;
  }

  private loadDats(stationId: string, time: number, day: HistoryDay): void {
    this.monitoring.getDatDevices(stationId, time, day).subscribe((devices) => this.dats.set(devices));
  }

  shiftWindow(deltaMinutes: number): void {
    // On rafraîchit la borne à chaque navigation : le temps continue de s'écouler pendant
    // que l'ASCT consulte l'écran, et on ne doit jamais laisser dépasser le créneau courant.
    const bound = this.computeNowMinutes(this.selectedDay());
    this.nowMinutes.set(bound);

    let next = this.centerMinutes() + deltaMinutes;
    if (bound != null && next > bound) {
      next = bound;
    }
    if (next === this.centerMinutes()) {
      return;
    }

    this.centerMinutes.set(next);
    this.window.set(this.monitoring.getDatWindow(next));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { time: next, day: this.selectedDay() },
      queryParamsHandling: 'merge',
    });
    this.loadDats(this.stationId(), next, this.selectedDay());
  }

  refresh(): void {
    if (this.refreshing()) {
      return;
    }
    this.nowMinutes.set(this.computeNowMinutes(this.selectedDay()));
    this.refreshing.set(true);
    this.monitoring
      .getDatDevices(this.stationId(), this.centerMinutes(), this.selectedDay())
      .pipe(finalize(() => this.refreshing.set(false)))
      .subscribe((devices) => this.dats.set(devices));
  }

  onSegmentClick(dat: DatDevice, segment: DatTimelineSegment): void {
    if (!segment.detail) {
      return;
    }
    this.router.navigate(
      ['/gare', this.stationId(), 'dats', dat.id, 'detail'],
      {
        queryParams: {
          start: segment.startMinutes,
          time: this.centerMinutes(),
          day: this.selectedDay(),
        },
      },
    );
  }

  goBack(): void {
    this.router.navigate(['/gare', this.stationId(), 'historique']);
  }
}
